import React, { useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ArrowLeft, Loader2, ExternalLink, Star, Zap, RefreshCw, AlertTriangle, Hourglass, FileDown } from "lucide-react";
import { toast } from "sonner";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, Legend,
} from "recharts";
import { fmtNum, fmtPts, playerMinutes } from "@/lib/datasetFmt";
import { exportRivalPlayerPdf } from "@/lib/export";
import RivalScoutingReports from "@/components/RivalScoutingReports";

const PROGRESS_LABEL = {
  PENDIENTE: "Pendiente",
  ANALIZANDO_FICHA: "Analizando ficha",
  BUSCANDO_TEMPORADAS: "Buscando temporadas",
  ANALIZANDO_PARTIDOS: "Analizando partidos",
  ANALIZANDO_ACTAS: "Analizando actas",
  CALCULANDO_ESTADISTICAS: "Calculando estadísticas",
  COMPLETADO: "Completado",
  ERROR: "Error",
};

const KPI = ({ label, value, testId }) => (
  <Card className="border-slate-200" data-testid={testId}>
    <CardContent className="p-3 text-center">
      <div className="text-xl font-black text-purple-800">{value}</div>
      <div className="text-[9px] uppercase tracking-widest text-slate-500 mt-0.5">{label}</div>
    </CardContent>
  </Card>
);

const PctBar = ({ label, pct, detail, testId }) => (
  <div data-testid={testId}>
    <div className="flex items-center justify-between text-xs mb-1">
      <span className="font-bold text-slate-700">{label}</span>
      <span className="font-black text-purple-800">{pct != null ? `${pct}%` : "N/D"}</span>
    </div>
    <div className="h-2.5 rounded-full bg-slate-100 overflow-hidden">
      <div className="h-full rounded-full" style={{ width: `${Math.min(pct || 0, 100)}%`, background: "linear-gradient(90deg,#5B2383,#D9B300)" }} />
    </div>
    {detail && <div className="text-[10px] text-slate-400 mt-0.5">{detail}</div>}
  </div>
);

const seasonMinutes = (s) => (s?.minutos_app != null ? s.minutos_app : null);

function quickReadTags(p) {
  const tags = [];
  const conv = p.convocado_total || 0;
  const jug = p.jugados_total || 0;
  if (p.ranking_general != null && p.ranking_general <= 10) tags.push("Top 10 ranking");
  if (conv > 0 && (p.titular_total || 0) / conv >= 0.7) tags.push("Alta titularidad");
  if (conv > 0 && jug / conv >= 0.8) tags.push("Alta participación");
  if ((p.temporadas_analizadas || 0) >= 3) tags.push(`${p.temporadas_analizadas} temporadas analizadas`);
  if (jug >= 60) tags.push("Alta continuidad");
  if (jug > 0 && (p.goles_total || 0) / jug >= 0.5) tags.push("Goleador");
  if (p.minutos_disponibles === "Sí") tags.push("Minutos disponibles");
  return tags;
}

export default function RivalDatasetPlayer() {
  const { jugadorId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [p, setP] = useState(null);
  const [loading, setLoading] = useState(true);
  const [job, setJob] = useState(null);
  const [actas, setActas] = useState([]);
  const [selSeason, setSelSeason] = useState("total");
  const [rffmTab, setRffmTab] = useState("resumen");
  const pollRef = useRef(null);

  const loadAll = () => {
    api.get(`/rival-dataset/players/${jugadorId}`).then((r) => setP(r.data)).catch(() => {});
    api.get(`/rival-dataset/players/${jugadorId}/rffm-matches`).then((r) => setActas(r.data)).catch(() => {});
    api.get(`/rffm/reports/player/${jugadorId}/status`).then((r) => setJob(r.data)).catch(() => {});
  };

  useEffect(() => {
    setLoading(true);
    Promise.all([
      api.get(`/rival-dataset/players/${jugadorId}`).then((r) => setP(r.data)),
      api.get(`/rival-dataset/players/${jugadorId}/rffm-matches`).then((r) => setActas(r.data)).catch(() => {}),
      api.get(`/rffm/reports/player/${jugadorId}/status`).then((r) => setJob(r.data)).catch(() => {}),
    ]).catch((err) => toast.error(formatApiError(err)))
      .finally(() => setLoading(false));
    return () => clearInterval(pollRef.current);
  }, [jugadorId]);

  const jobActive = job && ["PENDIENTE", "PROCESANDO"].includes(job.status);

  useEffect(() => {
    clearInterval(pollRef.current);
    if (jobActive) {
      pollRef.current = setInterval(async () => {
        try {
          const { data } = await api.get(`/rffm/reports/player/${jugadorId}/status`);
          setJob(data);
          if (data.status === "COMPLETADO") {
            toast.success("Informe RFFM completado");
            loadAll();
          } else if (data.status === "ERROR") {
            toast.error(`Informe RFFM: ${data.error || "error de lectura"}`);
            loadAll();
          }
        } catch { /* siguiente intento */ }
      }, 4000);
    }
    return () => clearInterval(pollRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobActive, jugadorId]);

  const startSync = async () => {
    try {
      const { data } = await api.post(`/rffm/reports/player/${jugadorId}`);
      setJob(data);
      toast.success("Análisis RFFM en cola — se actualizará automáticamente");
    } catch (err) { toast.error(formatApiError(err)); }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24" data-testid="rival-player-loading">
        <Loader2 className="h-8 w-8 animate-spin text-purple-600" />
      </div>
    );
  }
  if (!p) return <p className="text-sm text-slate-400 py-10 text-center">Jugador no encontrado.</p>;

  const conv0 = p.convocado_total || 0;
  const mins0 = playerMinutes(p);
  void conv0;
  const seasons = p.seasons || [];
  const seasonSel = selSeason === "total" ? null : seasons.find((s) => s.temporada === selSeason);
  const view = seasonSel
    ? {
      convocado: seasonSel.convocado, titular: seasonSel.titular, suplente: seasonSel.suplente,
      jugados: seasonSel.jugados, goles: seasonSel.goles, minutos: seasonMinutes(seasonSel),
      amarillas: seasonSel.amarillas, rojas: seasonSel.rojas,
    }
    : {
      convocado: p.convocado_total, titular: p.titular_total, suplente: p.suplente_total,
      jugados: p.jugados_total, goles: p.goles_total, minutos: mins0,
      amarillas: p.amarillas_total, rojas: p.rojas_total,
    };
  const conv = view.convocado || 0;
  const jug = view.jugados || 0;
  const mins = view.minutos;
  const pctTitular = conv > 0 && view.titular != null ? Math.round((view.titular / conv) * 100) : null;
  const pctParticipa = conv > 0 && jug ? Math.round((jug / conv) * 100) : null;
  const golesPartido = jug > 0 && view.goles != null ? (view.goles / jug).toFixed(2).replace(".", ",") : null;
  const minPartido = jug > 0 && mins != null ? Math.round(mins / jug) : null;

  const readStatus = p.rffm_read_status || "";
  const analizado = !!p.rffm_matches_synced || seasons.length > 0;
  const isRffmPlayer = !!p.codigo_rffm;
  const isError = readStatus.startsWith("ERROR");
  const placeholder = jobActive ? "…"
    : isRffmPlayer && !analizado && isError ? "Error"
      : isRffmPlayer && !analizado ? "Pendiente"
        : analizado ? "No disp." : "N/D";
  const vd = (v) => (v != null ? fmtNum(v) : placeholder);

  const chartData = seasons.map((s) => ({
    temporada: (s.temporada || "").replace("20", "").replace("-20", "/"),
    partidos: s.jugados ?? 0,
    Convocado: s.convocado ?? 0,
    Jugados: s.jugados ?? 0,
    Titular: s.titular ?? 0,
    Goles: s.goles ?? 0,
    Amarillas: s.amarillas ?? 0,
    Rojas: s.rojas ?? 0,
    goles: s.goles ?? 0,
    minutos: seasonMinutes(s),
  }));
  const minutesSeasons = chartData.filter((c) => c.minutos != null);
  const tags = quickReadTags(p);

  return (
    <div className="space-y-5" data-testid="rival-player-dashboard-page">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <Button variant="ghost" onClick={() => navigate(-1)} data-testid="rival-player-back">
          <ArrowLeft className="h-4 w-4 mr-2" /> Volver
        </Button>
        {p.ficha_rffm && (
          <div className="flex gap-2 flex-wrap">
            {isAdmin && isRffmPlayer && (
              <Button onClick={startSync} disabled={jobActive}
                className="bg-purple-700 hover:bg-purple-800 text-white font-bold" data-testid="rival-player-rffm-sync">
                {jobActive ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-2" />}
                {jobActive ? (PROGRESS_LABEL[job?.progress] || "Procesando") : "Actualizar datos RFFM"}
              </Button>
            )}
            <Button variant="outline" onClick={() => exportRivalPlayerPdf(jugadorId, p.jugador)}
              className="border-purple-300 text-purple-700 hover:bg-purple-50 font-bold" data-testid="rival-player-report-pdf">
              <FileDown className="h-4 w-4 mr-2" /> Informe PDF
            </Button>
            <a href={p.ficha_rffm} target="_blank" rel="noreferrer">
              <Button variant="outline" className="border-purple-300 text-purple-700 hover:bg-purple-50 font-bold" data-testid="rival-player-rffm">
                <ExternalLink className="h-4 w-4 mr-2" /> Ver ficha RFFM
              </Button>
            </a>
          </div>
        )}
      </div>

      {/* Estado del análisis RFFM */}
      {jobActive && (
        <div className="rounded-xl border border-purple-200 bg-purple-50 p-4 flex items-center gap-3" data-testid="rffm-analysis-progress">
          <Loader2 className="h-5 w-5 animate-spin text-purple-700 shrink-0" />
          <div className="text-sm">
            <div className="font-bold text-purple-900">Generando informe individual RFFM — {PROGRESS_LABEL[job?.progress] || "Procesando"}</div>
            {job?.progress_detail && <div className="text-xs text-purple-700">{job.progress_detail}</div>}
          </div>
        </div>
      )}
      {!jobActive && isRffmPlayer && !analizado && !isError && (
        <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 flex items-center gap-3 flex-wrap" data-testid="rffm-pending-banner">
          <Hourglass className="h-5 w-5 text-amber-600 shrink-0" />
          <div className="text-sm flex-1 min-w-0">
            <div className="font-bold text-amber-900">Pendiente de analizar</div>
            <div className="text-xs text-amber-700">Este jugador aún no tiene informe individual RFFM (temporadas, partidos y actas).</div>
          </div>
          {isAdmin && (
            <Button size="sm" onClick={startSync} className="bg-amber-600 hover:bg-amber-700 text-white font-bold" data-testid="rffm-pending-analyze">
              Analizar ahora
            </Button>
          )}
        </div>
      )}
      {!jobActive && isRffmPlayer && !analizado && isError && (
        <div className="rounded-xl border border-rose-200 bg-rose-50 p-4 flex items-center gap-3 flex-wrap" data-testid="rffm-error-banner">
          <AlertTriangle className="h-5 w-5 text-rose-600 shrink-0" />
          <div className="text-sm flex-1 min-w-0">
            <div className="font-bold text-rose-900">Error de lectura RFFM</div>
            <div className="text-xs text-rose-700">{job?.error || "No se pudo completar el análisis. Puedes reintentarlo."}</div>
          </div>
          {isAdmin && (
            <Button size="sm" onClick={startSync} className="bg-rose-600 hover:bg-rose-700 text-white font-bold" data-testid="rffm-error-retry">
              Reintentar
            </Button>
          )}
        </div>
      )}

      {/* Header */}
      <div className="rounded-2xl text-white p-6" style={{ background: "linear-gradient(120deg, #3d1758, #5B2383)" }} data-testid="rival-player-header">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="min-w-0">
            <h1 className="text-2xl sm:text-3xl font-black leading-tight" style={{ fontFamily: "Nunito" }} data-testid="rival-player-name">
              {p.jugador}
            </h1>
            <div className="text-sm text-purple-100 mt-1 font-semibold">{p.rival_actual || "Sin club"}</div>
            <div className="flex flex-wrap gap-2 mt-3">
              {p.equipo_ef && <Badge className="bg-white/20 text-white border-0">Rival de {p.equipo_ef}</Badge>}
              {p.anio_nacimiento && <Badge className="bg-white/20 text-white border-0">{p.anio_nacimiento}</Badge>}
              {p.posicion && <Badge className="bg-white/20 text-white border-0">{p.posicion}</Badge>}
              {p.codigo_rffm && <Badge className="bg-white/10 text-purple-100 border-0">RFFM {p.codigo_rffm}</Badge>}
              {p.veces_destacado > 0 && (
                <Badge className="border-0 text-black font-bold" style={{ background: "#D9B300" }}>
                  <Star className="h-3 w-3 mr-1" /> {p.veces_destacado}× destacado
                </Badge>
              )}
            </div>
          </div>
          <div className="flex gap-3 shrink-0">
            <div className="rounded-xl bg-white/10 border border-white/20 px-4 py-2 text-center">
              <div className="text-2xl font-black" style={{ color: "#D9B300" }} data-testid="rival-player-ranking">
                {p.ranking_general != null ? `#${p.ranking_general}` : "—"}
              </div>
              <div className="text-[9px] uppercase tracking-widest text-purple-100">Ranking</div>
            </div>
            <div className="rounded-xl bg-white/10 border border-white/20 px-4 py-2 text-center">
              <div className="text-2xl font-black" data-testid="rival-player-puntuacion">{fmtPts(p.puntuacion)}</div>
              <div className="text-[9px] uppercase tracking-widest text-purple-100">Puntuación</div>
            </div>
          </div>
        </div>
      </div>

      {/* Selector de temporada */}
      {seasons.length > 0 && (
        <div className="flex gap-1.5 flex-wrap" data-testid="rffm-season-selector">
          <button onClick={() => setSelSeason("total")}
            className={`px-3 py-1.5 rounded-full text-xs font-bold border ${selSeason === "total" ? "bg-purple-700 text-white border-purple-700" : "bg-white text-slate-600 border-slate-200 hover:border-purple-300"}`}
            data-testid="season-tab-total">
            Total {seasons.length} temporada{seasons.length > 1 ? "s" : ""}
          </button>
          {[...seasons].reverse().map((s) => (
            <button key={s.temporada} onClick={() => setSelSeason(s.temporada)}
              className={`px-3 py-1.5 rounded-full text-xs font-bold border ${selSeason === s.temporada ? "bg-purple-700 text-white border-purple-700" : "bg-white text-slate-600 border-slate-200 hover:border-purple-300"}`}
              data-testid={`season-tab-${s.temporada}`}>
              {s.temporada}
            </button>
          ))}
        </div>
      )}

      {/* KPIs */}
      <div className="grid grid-cols-4 gap-2 sm:gap-3">
        <KPI label="Convocado" value={vd(view.convocado)} testId="kpi-convocado" />
        <KPI label="Titular" value={vd(view.titular)} testId="kpi-titular" />
        <KPI label="Suplente" value={vd(view.suplente)} testId="kpi-suplente" />
        <KPI label="Partidos" value={vd(view.jugados)} testId="kpi-partidos" />
        <KPI label="Goles" value={vd(view.goles)} testId="kpi-goles" />
        <KPI label="Minutos" value={vd(mins)} testId="kpi-minutos" />
        <KPI label="Amarillas" value={vd(view.amarillas)} testId="kpi-amarillas" />
        <KPI label="Rojas" value={vd(view.rojas)} testId="kpi-rojas" />
      </div>

      {/* Percentages */}
      <Card className="border-slate-200">
        <CardContent className="p-5 space-y-4">
          <PctBar label="% Titular" pct={pctTitular} detail={pctTitular != null ? `${fmtNum(view.titular)} titularidades de ${fmtNum(conv)} convocatorias` : null} testId="pct-titular" />
          <PctBar label="% Participación" pct={pctParticipa} detail={pctParticipa != null ? `${fmtNum(jug)} partidos jugados de ${fmtNum(conv)} convocatorias` : null} testId="pct-participacion" />
          <div className="grid grid-cols-2 gap-3 pt-1">
            <div className="rounded-lg bg-purple-50 p-3 text-center" data-testid="ratio-goles-partido">
              <div className="text-lg font-black text-purple-800">{golesPartido ?? placeholder}</div>
              <div className="text-[9px] uppercase tracking-widest text-slate-500">Goles / partido</div>
            </div>
            <div className="rounded-lg bg-purple-50 p-3 text-center" data-testid="ratio-min-partido">
              <div className="text-lg font-black text-purple-800">{minPartido != null ? fmtNum(minPartido) : placeholder}</div>
              <div className="text-[9px] uppercase tracking-widest text-slate-500">Minutos / partido</div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Evolution */}
      {seasons.length > 0 && (
        <div>
          <h2 className="text-base md:text-lg font-black mb-3" style={{ fontFamily: "Nunito" }}>Evolución</h2>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            {seasons.map((s) => (
              <Card key={s.temporada} className="border-slate-200" data-testid={`season-card-${s.temporada}`}>
                <CardContent className="p-4">
                  <div className="font-black text-purple-800" style={{ fontFamily: "Nunito" }}>{s.temporada}</div>
                  <div className="text-[11px] text-slate-500 truncate">{s.equipo_temporada || "—"}</div>
                  <div className="text-[10px] text-slate-400 truncate mb-2">{s.categoria || ""}</div>
                  <div className="grid grid-cols-2 gap-x-3 gap-y-1 text-xs">
                    <span className="text-slate-500">Convocado</span><span className="font-bold text-right">{fmtNum(s.convocado)}</span>
                    <span className="text-slate-500">Titular</span><span className="font-bold text-right">{fmtNum(s.titular)}</span>
                    <span className="text-slate-500">Suplente</span><span className="font-bold text-right">{fmtNum(s.suplente)}</span>
                    <span className="text-slate-500">Partidos</span><span className="font-bold text-right">{fmtNum(s.jugados)}</span>
                    <span className="text-slate-500">Goles</span><span className="font-bold text-right">{fmtNum(s.goles)}</span>
                    <span className="text-slate-500">Minutos</span><span className="font-bold text-right">{fmtNum(seasonMinutes(s))}</span>
                    <span className="text-slate-500">Amarillas</span><span className="font-bold text-right">{fmtNum(s.amarillas)}</span>
                    <span className="text-slate-500">Rojas</span><span className="font-bold text-right">{fmtNum(s.rojas)}</span>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      )}

      {/* Charts */}
      {chartData.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Card className="border-slate-200" data-testid="chart-partidos">
            <CardContent className="p-4">
              <div className="text-xs font-black uppercase tracking-widest text-slate-500 mb-2">Participación RFFM por temporada</div>
              <ResponsiveContainer width="100%" height={180}>
                <BarChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} />
                  <XAxis dataKey="temporada" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 10 }} allowDecimals={false} />
                  <Tooltip />
                  <Legend wrapperStyle={{ fontSize: 10 }} />
                  <Bar dataKey="Convocado" fill="#c4b5fd" radius={[4, 4, 0, 0]} maxBarSize={22} />
                  <Bar dataKey="Jugados" fill="#7c3aed" radius={[4, 4, 0, 0]} maxBarSize={22} />
                  <Bar dataKey="Titular" fill="#3d1758" radius={[4, 4, 0, 0]} maxBarSize={22} />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
          <Card className="border-slate-200" data-testid="chart-goles">
            <CardContent className="p-4">
              <div className="text-xs font-black uppercase tracking-widest text-slate-500 mb-2">Goles y tarjetas RFFM</div>
              <ResponsiveContainer width="100%" height={180}>
                <BarChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} />
                  <XAxis dataKey="temporada" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 10 }} allowDecimals={false} />
                  <Tooltip />
                  <Legend wrapperStyle={{ fontSize: 10 }} />
                  <Bar dataKey="Goles" fill="#D9B300" radius={[4, 4, 0, 0]} maxBarSize={22} />
                  <Bar dataKey="Amarillas" fill="#facc15" radius={[4, 4, 0, 0]} maxBarSize={22} />
                  <Bar dataKey="Rojas" fill="#e11d48" radius={[4, 4, 0, 0]} maxBarSize={22} />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
          {minutesSeasons.length > 0 && (
            <Card className="border-slate-200 md:col-span-2" data-testid="chart-minutos">
              <CardContent className="p-4">
                <div className="text-xs font-black uppercase tracking-widest text-slate-500 mb-2">Evolución de minutos</div>
                <ResponsiveContainer width="100%" height={180}>
                  <BarChart data={minutesSeasons}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} />
                    <XAxis dataKey="temporada" tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 10 }} allowDecimals={false} />
                    <Tooltip />
                    <Bar dataKey="minutos" fill="#5B2383" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {/* Quick read */}
      {tags.length > 0 && (
        <Card className="border-slate-200" data-testid="quick-read">
          <CardContent className="p-4">
            <div className="text-xs font-black uppercase tracking-widest text-slate-500 mb-2 flex items-center gap-1.5">
              <Zap className="h-3.5 w-3.5" style={{ color: "#D9B300" }} /> Lectura rápida
            </div>
            <div className="flex flex-wrap gap-2">
              {tags.map((t) => (
                <span key={t} className="px-3 py-1 rounded-full text-xs font-bold text-white" style={{ background: "#5B2383" }}>
                  {t}
                </span>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Datos RFFM — resumen y actas */}
      {(analizado || actas.length > 0) && (
        <div data-testid="rffm-data-section">
          <h2 className="text-base md:text-lg font-black mb-2" style={{ fontFamily: "Nunito" }}>Datos RFFM</h2>
          <div className="flex gap-1 border-b border-slate-200 mb-3">
            {[["resumen", "Resumen"], ["actas", `Actas (${actas.length})`]].map(([k, l]) => (
              <button key={k} onClick={() => setRffmTab(k)}
                className={`px-3 py-2 font-bold text-sm -mb-px border-b-2 ${rffmTab === k ? "border-purple-700 text-purple-800" : "border-transparent text-slate-500"}`}
                data-testid={`rffm-data-tab-${k}`}>{l}</button>
            ))}
          </div>
          {rffmTab === "resumen" && (
            <Card className="border-slate-200 overflow-hidden">
              <CardContent className="p-0 overflow-x-auto">
                <table className="w-full text-xs">
                  <thead className="bg-slate-50 border-b border-slate-200">
                    <tr>{["Temporada", "Equipo", "Part. equipo", "Conv.", "Titular", "Jugados", "Minutos", "Goles", "TA", "2ªA", "TR", "Cobertura actas"].map((h) => (
                      <th key={h} className="px-2.5 py-2 text-left text-[10px] uppercase tracking-widest text-slate-500 whitespace-nowrap">{h}</th>
                    ))}</tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {[...seasons].reverse().map((s) => (
                      <tr key={s.temporada} data-testid={`rffm-resumen-${s.temporada}`}>
                        <td className="px-2.5 py-2 font-bold text-purple-800 whitespace-nowrap">{s.temporada}</td>
                        <td className="px-2.5 py-2 max-w-[160px] truncate">{s.equipo_temporada || "—"}</td>
                        <td className="px-2.5 py-2">{s.partidos_equipo ?? "—"}</td>
                        <td className="px-2.5 py-2">{fmtNum(s.convocado)}</td>
                        <td className="px-2.5 py-2">{fmtNum(s.titular)}</td>
                        <td className="px-2.5 py-2">{fmtNum(s.jugados)}</td>
                        <td className="px-2.5 py-2 whitespace-nowrap">
                          {s.minutos_app != null ? fmtNum(s.minutos_app) : "No disp."}
                          {s.minutos_estado && s.minutos_estado !== "Oficial RFFM" && (
                            <span className="ml-1 text-[9px] text-slate-400">({s.minutos_estado})</span>
                          )}
                        </td>
                        <td className="px-2.5 py-2">{fmtNum(s.goles)}</td>
                        <td className="px-2.5 py-2">{fmtNum(s.amarillas)}</td>
                        <td className="px-2.5 py-2">{s.dobles_amarillas ?? "—"}</td>
                        <td className="px-2.5 py-2">{fmtNum(s.rojas)}</td>
                        <td className="px-2.5 py-2">{s.cobertura_actas != null ? `${s.cobertura_actas}%` : "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {seasons.length === 0 && <div className="p-6 text-center text-slate-400 text-sm">Sin temporadas analizadas.</div>}
              </CardContent>
            </Card>
          )}
          {rffmTab === "actas" && (
            <Card className="border-slate-200 overflow-hidden">
              <CardContent className="p-0 overflow-x-auto">
                <table className="w-full text-xs">
                  <thead className="bg-slate-50 border-b border-slate-200">
                    <tr>{["Temp.", "J", "Fecha", "Rival", "Resultado", "Conv.", "Titular", "Min", "Goles", "TA", "TR", "Acta"].map((h) => (
                      <th key={h} className="px-2.5 py-2 text-left text-[10px] uppercase tracking-widest text-slate-500 whitespace-nowrap">{h}</th>
                    ))}</tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {actas
                      .filter((r) => selSeason === "total" || r.season_label === selSeason)
                      .map((r) => (
                        <tr key={r.match_id + r.season_id} className={r.called_up ? "" : "opacity-50"} data-testid={`rffm-acta-${r.match_id}`}>
                          <td className="px-2.5 py-2 whitespace-nowrap">{(r.season_label || "").replace("20", "").replace("-20", "/")}</td>
                          <td className="px-2.5 py-2">{r.round || "—"}</td>
                          <td className="px-2.5 py-2 whitespace-nowrap">{r.date || "—"}</td>
                          <td className="px-2.5 py-2 max-w-[180px] truncate font-semibold">{r.opponent || "—"}</td>
                          <td className="px-2.5 py-2 whitespace-nowrap">{r.result || "—"}{r.home_away === "casa" ? " (C)" : " (F)"}</td>
                          <td className="px-2.5 py-2">{r.called_up ? "Sí" : "No"}</td>
                          <td className="px-2.5 py-2">{r.called_up ? (r.starter ? "Titular" : (r.played ? "Suplente (entra)" : "Suplente")) : "—"}</td>
                          <td className="px-2.5 py-2 font-bold">
                            {r.minutes != null ? `${r.minutes}'` : (r.played ? "N/V" : (r.called_up ? "0'" : "—"))}
                          </td>
                          <td className="px-2.5 py-2">{r.goals || 0}</td>
                          <td className="px-2.5 py-2">{(r.yellow_cards || 0) + (r.second_yellow || 0)}</td>
                          <td className="px-2.5 py-2">{(r.red_cards || 0) + (r.second_yellow || 0)}</td>
                          <td className="px-2.5 py-2">
                            {r.report_url && (
                              <a href={r.report_url} target="_blank" rel="noreferrer" className="text-purple-700 font-bold hover:underline whitespace-nowrap" data-testid={`acta-link-${r.match_id}`}>
                                Ver acta
                              </a>
                            )}
                          </td>
                        </tr>
                      ))}
                  </tbody>
                </table>
                {actas.filter((r) => selSeason === "total" || r.season_label === selSeason).length === 0 && (
                  <div className="p-6 text-center text-slate-400 text-sm">
                    {analizado ? "No hay actas disponibles en RFFM para esta selección." : "Pendiente de analizar."}
                  </div>
                )}
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {/* Scouting reports */}
      <RivalScoutingReports jugadorId={jugadorId} />

      {/* Internal notes (manual players) */}
      {p.notas && (
        <Card className="border-amber-200 bg-amber-50" data-testid="rival-player-notes">
          <CardContent className="p-4 text-sm text-slate-700 whitespace-pre-line">
            <div className="text-[10px] uppercase tracking-widest text-amber-700 font-bold mb-1">Notas de scouting</div>
            {p.notas}
          </CardContent>
        </Card>
      )}

      {/* Featured in matches */}
      {(p.destacados || []).length > 0 && (
        <Card className="border-slate-200" data-testid="rival-player-featured">
          <CardContent className="p-4">
            <div className="text-xs font-black uppercase tracking-widest text-slate-500 mb-2 flex items-center gap-1.5">
              <Star className="h-3.5 w-3.5" style={{ color: "#D9B300" }} /> Destacado en partidos
            </div>
            <ul className="divide-y divide-slate-100">
              {p.destacados.map((d) => (
                <li key={d.id} className="py-2.5 flex items-center justify-between gap-2 text-sm">
                  <div className="min-w-0">
                    <div className="font-semibold truncate">{d.match.team_name || "EF Concepción"} vs {d.match.rival}</div>
                    <div className="text-xs text-slate-500">
                      {d.match.date}{d.match.result ? ` · ${d.match.result}` : ""}
                      {d.observaciones ? ` · ${d.observaciones}` : ""}
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
